import React from "react";
import styled from "@emotion/styled";

/**
 * On/off switch.
 *
 * Extracted from JohnDeereProfileSection, which grew the first one inline. It
 * moved here rather than being copied a second time — two visually identical
 * switches that drift apart is exactly the kind of inconsistency nobody notices
 * until both are on screen together.
 *
 * A <button role="switch">, not a styled checkbox, so it is keyboard-operable
 * and screen readers announce its state.
 */

// `on` drives the styling only — it must not reach the DOM. Emotion's default
// filter lets it through (`isPropValid("on") === true`), which would render
// <button on="true">, so both styled parts opt out explicitly.
const styleOnly = { shouldForwardProp: (prop: string) => prop !== "on" };

const Track = styled("button", styleOnly)<{ on: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
  background: ${(p) => (p.on ? "#16a34a" : "#cbd5e1")};
  transition: background 0.15s ease;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Thumb = styled("span", styleOnly)<{ on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${(p) => (p.on ? "22px" : "2px")};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.15s ease;
`;

interface IProps {
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
}

const Switch: React.FC<IProps> = ({ on, onToggle, disabled, ariaLabel, className }) => (
  <Track
    on={on}
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={onToggle}
    className={className}
  >
    <Thumb on={on} />
  </Track>
);

export default Switch;
